// @flow

/*
 * ───────────────────────────────────────────────────────────────────────────
 * HOW TO PLUG IN REAL, GATED TAGS (prior blocking)
 *
 * `manageScriptTags: true` makes the library activate scripts ONLY after the
 * matching consent. Convert any non-essential tag to a managed tag by setting
 * `type="text/plain"` + `data-category`, then render it in public/index.html.
 * That is exactly how the Bitrix24 CRM form loader is gated today — see
 * public/index.html, `data-category="functionality"`.
 *
 *   // analytics — a tag not already inside the GTM container
 *   // <script type="text/plain" data-category="analytics" src="…" />
 *
 *   // functionality — chat / "request a call" widget
 *   // <script type="text/plain" data-category="functionality" src="…" />
 *
 * Google tags (GTM / GA4) are additionally governed by Consent Mode v2 — see
 * consentMode.js.
 * ───────────────────────────────────────────────────────────────────────────
 */

import { applyConsentMode, loadGtm } from './consentMode';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_EXPIRY_DAYS,
  CONSENT_EXPIRY_MONTHS,
  COOKIE_POLICY_PATH,
  LEGAL_ORIGIN,
  POLICY_VERSION,
  PRIVACY_POLICY_PATH,
} from './constants';
import { COOKIE_TABLE_ROWS } from './cookieTables';

/** Pick the active locale's text, falling back to the default locale (ro). */
function localize(text: { ro: string, ru: string }, locale: string): string {
  return text[locale] || text.ro;
}

/**
 * Build a category's cookie table from the audited inventory in cookieTables.js,
 * resolving each row's `name`/`desc`/`expiration` to the active locale. Returns
 * undefined for categories with no rows (so the section renders without an
 * empty table).
 */
function cookieTableFor(headers: Object, category: string, locale: string) {
  const rows = COOKIE_TABLE_ROWS[category];
  if (!rows || !rows.length) return undefined;
  const body = rows.map(row => ({
    // User-friendly localized label — technical cookie ids intentionally never
    // reach the UI (they live as code comments in cookieTables.js).
    name: localize(row.name, locale),
    domain: row.domain,
    desc: localize(row.desc, locale),
    expiration: localize(row.expiration, locale),
  }));
  return { headers, body };
}

/**
 * Build the vanilla-cookieconsent v3 configuration.
 *
 * Compliance-critical choices live here:
 *  - Optional categories default to OFF (`enabled` omitted/false). Only
 *    `necessary` is enabled + readOnly. Never pre-enable the others.
 *  - `equalWeightButtons: true` on BOTH modals so "Reject all" and "Accept all"
 *    have equal visual prominence and are both one click away on the first
 *    layer.
 *  - `manageScriptTags: true` — `<script type="text/plain" data-category>` tags
 *    run only after the matching consent (prior blocking).
 *  - `disablePageInteraction: true` — matches 999.md: the page behind is dimmed
 *    until an explicit choice is made.
 */
export function buildCookieConsentConfig({
  locale,
  t,
  onUserChoice,
}: {
  locale: string,
  onUserChoice?: (acceptedCategories: string[]) => void,
  t: Function,
}) {
  const tableHeaders = {
    name: t('tableName'),
    domain: t('tableDomain'),
    desc: t('tableDescription'),
    expiration: t('tableExpiration'),
  };

  // GDPR: the first-layer description must link to the Cookie Policy and the
  // Privacy Policy. The library renders descriptions as HTML. Both pages live on
  // the main site, hence the absolute origin.
  const policyLinks = `<p><a href="${LEGAL_ORIGIN}/${locale}${COOKIE_POLICY_PATH}" target="_blank" rel="noopener noreferrer">${t(
    'linkCookiePolicy',
  )}</a> · <a href="${LEGAL_ORIGIN}/${locale}${PRIVACY_POLICY_PATH}" target="_blank" rel="noopener noreferrer">${t(
    'linkPrivacyPolicy',
  )}</a></p>`;
  const consentDescription = `${t('consentDescription')} ${policyLinks}`;

  return {
    // Re-collect consent when the policy text changes (bump POLICY_VERSION).
    revision: POLICY_VERSION,

    // Opt-in (GDPR): nothing is granted and no optional script runs until the
    // user makes an explicit choice. Never switch to 'opt-out' — it treats
    // `enabled` categories as accepted before any interaction.
    mode: 'opt-in',
    autoShow: true,
    manageScriptTags: true,
    disablePageInteraction: true,

    cookie: {
      name: CONSENT_COOKIE_NAME,
      expiresAfterDays: CONSENT_EXPIRY_DAYS,
      sameSite: 'Lax',
      // domain/path default to the current host & '/'. Deliberately NOT set to
      // '.999.md': 999.md writes its own host-only cc_consent and a wildcard
      // cookie here would shadow it.
    },

    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      // Strictly necessary — always on, no choice.
      necessary: {
        enabled: true,
        readOnly: true,
      },
      // Optional — MUST stay OFF by default (do not add `enabled: true`):
      // pre-enabled categories mean pre-ticked toggles and, in opt-out mode,
      // scripts running before consent.
      functionality: {},
      analytics: {
        // GA first-party cookies.
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: /^_gat/ }],
        },
      },
    },

    language: {
      // Default language follows the app locale (ro/ru).
      default: locale,
      translations: {
        [locale]: {
          consentModal: {
            title: t('consentTitle'),
            description: consentDescription,
            acceptAllBtn: t('btnAcceptAll'),
            // The first layer offers three plainly-labelled choices: accept
            // all / reject all / settings. The softer "accept only what's
            // needed" wording it used to carry was dropped (CS9-537) — the
            // action is unchanged (the library grants zero optional categories),
            // but a GDPR reject must read as a reject. (999.md intercepts this
            // button so it grants everything EXCEPT advertising; there is no
            // advertising category here, so that remap would silently mean
            // "accept all" and is intentionally absent — see index.js.)
            acceptNecessaryBtn: t('btnRejectAll'),
            showPreferencesBtn: t('btnCustomize'),
            // No close (X) icon on the first layer on purpose: the user must make
            // an explicit choice.
          },
          preferencesModal: {
            title: t('prefTitle'),
            acceptAllBtn: t('btnAcceptAll'),
            // Same label and same action as the first layer: grant zero
            // optional categories. (Mirrors 999-web-app, commit "add decline all
            // btn in modal of consent settings".)
            acceptNecessaryBtn: t('btnRejectAll'),
            savePreferencesBtn: t('btnSave'),
            closeIconLabel: t('close'),
            sections: [
              {
                title: t('prefIntroTitle'),
                description: t('consentDescription'),
              },
              {
                title: t('catNecessaryTitle'),
                description: t('catNecessaryDesc'),
                linkedCategory: 'necessary',
                cookieTable: cookieTableFor(tableHeaders, 'necessary', locale),
              },
              {
                title: t('catFunctionalityTitle'),
                description: t('catFunctionalityDesc'),
                linkedCategory: 'functionality',
                cookieTable: cookieTableFor(
                  tableHeaders,
                  'functionality',
                  locale,
                ),
              },
              {
                title: t('catAnalyticsTitle'),
                description: t('catAnalyticsDesc'),
                linkedCategory: 'analytics',
                cookieTable: cookieTableFor(tableHeaders, 'analytics', locale),
              },
              {
                // Keep the number in sync with CONSENT_EXPIRY_MONTHS.
                description: t('prefStorageNote', {
                  months: CONSENT_EXPIRY_MONTHS,
                }),
              },
            ],
          },
        },
      },
    },

    // --- Callbacks ---------------------------------------------------------

    // Fires on first consent AND on every page load with valid consent. Re-sync
    // Google Consent Mode each time, then bring in the prior-blocked GTM
    // container — a choice now exists, whatever it was (CS9-537). Order matters:
    // the update must be queued in dataLayer before gtm.js runs. Not a user
    // action — no onUserChoice here.
    onConsent: ({ cookie }: Object) => {
      applyConsentMode(cookie.categories);
      loadGtm();
    },

    // Fires exactly once, when the user makes their very first choice.
    onFirstConsent: ({ cookie }: Object) => {
      if (onUserChoice) onUserChoice(cookie.categories);
    },

    // Fires whenever the user changes a preference. Re-sync Consent Mode and
    // notify listeners of the new choice.
    onChange: ({ cookie }: Object) => {
      applyConsentMode(cookie.categories);
      // No-op unless this is the rare change made before onConsent ever ran.
      loadGtm();
      if (onUserChoice) onUserChoice(cookie.categories);
    },
  };
}
